import React, { useCallback } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-types-papi';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks';
import {
  withVerification,
  WithVerificationProps,
} from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import EmptyState from '../../../../EmptyState';

import s from '../EmptyState/EmptyState.scss';

export interface Props {
  view?: 'wide' | 'large';
  total: number;
  user: PapiUser;
  isOwner: boolean;
  enableAllCommissions(): void;
  onOpenChat: (user: PapiUser) => void;
}

export const DisabledEmptyState: React.FC<Props & WithVerificationProps> = ({
  view = 'large',
  total,
  isOwner,
  user,
  user: { username },
  enableAllCommissions,
  isVerificationNeeded,
  pushVerificationPopup,
  onOpenChat,
}) => {
  const { t } = useTranslation();
  const isChatFlagEnabled = useFlag('chat');

  const navigateToNotes = useCallback(() => {
    if (isVerificationNeeded) {
      pushVerificationPopup('notes');
      return;
    }

    const notesPath = Url.userNoteLink(username);
    window.location.replace(notesPath);
  }, [username, isVerificationNeeded, pushVerificationPopup]);

  return (
    <EmptyState
      view={view}
      className={c(s['root'], view === 'large' && s['large'])}
      titleClassName={s['title']}
      subtitleClassName={s['subtitle']}
      title={
        isOwner
          ? t!('pages.shop.disabledState.commissions.titleOwner')
          : t!('pages.shop.disabledState.commissions.title', { username })
      }
      subtitle={
        isOwner
          ? t!('pages.shop.disabledState.commissions.subtitleOwner', {
              count: total,
            })
          : t!('pages.shop.disabledState.commissions.subtitle')
      }
      buttonLabel={
        isOwner
          ? t!('pages.shop.disabledState.commissions.enableAllCommissions')
          : isChatFlagEnabled
          ? t!('common.openChat')
          : t!('pages.shop.disabledState.commissions.messageArtist')
      }
      onButtonClick={
        isOwner
          ? enableAllCommissions
          : isChatFlagEnabled
          ? () => onOpenChat(user)
          : navigateToNotes
      }
      withButton={true}
    />
  );
};

export default withVerification(DisabledEmptyState) as React.ComponentType<
  Props
>;
